import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowRight, ArrowDown, ArrowUpRight, Check, CheckCircle2, HelpCircle,
    Play, Pause, FastForward, SkipForward, Video, MousePointer2,
    MousePointerClick, Keyboard, ScrollText, FileText, Clock, Filter,
    Flame, Split, Zap, Layers, ShieldCheck, EyeOff, Lock, Server, Search, AlertTriangle, Star, Smartphone, Monitor, Globe, Users,
    Target, Timer, Route, ShoppingCart, CreditCard, UserPlus,
    CalendarDays, BarChart3, ChevronRight, Tablet
} from 'lucide-react';
import Header from '../../Components/Header';
import Footer from '../../Components/Footer';

/* ------------------------------------------------------------------
   인터랙티브 플레이어용 데모 데이터
   t: 진행률(0~100), x/y: 스테이지 내 커서 위치(%), 
   ------------------------------------------------------------------ */
const DEMO_DURATION = 48; // 초 (표시용)
const DEMO_WAYPOINTS = [
    { t: 0, x: 84, y: 8 },
    { t: 10, x: 70, y: 22 },
    { t: 18, x: 64, y: 30, event: { type: 'click', label: '색상 선택', icon: 'click' } },
    { t: 24, x: 66, y: 32 },
    { t: 30, x: 64, y: 30, event: { type: 'click', label: '색상 선택', icon: 'click' } },
    { t: 40, x: 56, y: 58, event: { type: 'scroll', label: '스크롤 ↓ 1,240px', icon: 'scroll' } },
    { t: 52, x: 58, y: 60 },
    { t: 70, x: 58, y: 60 }, // 비활동 구간 (52~70)
    { t: 78, x: 30, y: 78, event: { type: 'input', label: '수량 입력', icon: 'input' } },
    { t: 90, x: 72, y: 78, event: { type: 'click', label: '장바구니 담기', icon: 'click' } },
    { t: 100, x: 74, y: 80, event: { type: 'page', label: '/cart 이동', icon: 'page' } },
];
const DEMO_INACTIVE = { start: 52, end: 70 };

const interpolateCursor = (progress) => {
    const pts = DEMO_WAYPOINTS;
    if (progress <= 0) return pts[0];
    if (progress >= 100) return pts[pts.length - 1];
    for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i], b = pts[i + 1];
        if (progress >= a.t && progress <= b.t) {
            const r = (progress - a.t) / (b.t - a.t || 1);
            return { x: a.x + (b.x - a.x) * r, y: a.y + (b.y - a.y) * r };
        }
    }
    return pts[pts.length - 1];
};

const formatClock = (progress) => {
    const sec = Math.round((progress / 100) * DEMO_DURATION);
    return `0:${String(sec).padStart(2, '0')}`;
};

const ServiceReplay = () => {
    const [scrolled, setScrolled] = useState(false);

    // 인터랙티브 플레이어 상태
    const [progress, setProgress] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [speed, setSpeed] = useState(1);
    const [skipInactive, setSkipInactive] = useState(true);
    const [clickPulse, setClickPulse] = useState(null);
    const lastEventRef = useRef(-1);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // 데모 재생 루프
    useEffect(() => {
        if (!playing) return;
        const id = setInterval(() => {
            setProgress(prev => {
                let next = prev + 0.35 * speed;
                if (skipInactive && prev < DEMO_INACTIVE.start && next >= DEMO_INACTIVE.start) {
                    next = DEMO_INACTIVE.end;
                }
                if (next >= 100) {
                    setPlaying(false);
                    return 100;
                }
                return next;
            });
        }, 50);
        return () => clearInterval(id);
    }, [playing, speed, skipInactive]);

    // 클릭 이벤트 통과 시 리플 표시
    useEffect(() => {
        const idx = DEMO_WAYPOINTS.findIndex((p, i) => p.event?.type === 'click' && progress >= p.t && progress < p.t + 3 && i !== lastEventRef.current);
        if (idx !== -1) {
            lastEventRef.current = idx;
            const p = DEMO_WAYPOINTS[idx];
            setClickPulse({ x: p.x, y: p.y, key: Date.now() });
        }
        if (progress === 0) lastEventRef.current = -1;
    }, [progress]);

    const cursor = interpolateCursor(progress);
    const demoEvents = DEMO_WAYPOINTS.filter(p => p.event);

    const handleRestart = () => {
        setProgress(0);
        lastEventRef.current = -1;
        setPlaying(true);
    };

    const togglePlay = () => {
        if (progress >= 100) return handleRestart();
        setPlaying(p => !p);
    };

    // 활용 사례 — Tailwind purge 회피를 위해 클래스는 전부 정적 문자열로 둔다
    const useCases = [
        {
            icon: <ShoppingCart className="w-5 h-5" />,
            title: '상품 옵션 선택 실패',
            type: '구매 전환',
            problem: '히트맵에선 옵션 영역 클릭이 많아 보였지만 전환은 낮음 — 이유를 알 수 없었음',
            solution: '세션을 재생해 보니 모바일에서 옵션 드롭다운이 키보드에 가려 3~4번 반복 클릭 후 이탈',
            result: '옵션 UI 교체 후 장바구니 전환 1.6배',
            classes: {
                glow: 'bg-cyan-500/10',
                iconBox: 'text-cyan-400 group-hover:bg-cyan-600',
                badge: 'bg-cyan-900/30 text-cyan-400 border-cyan-500/20',
                result: 'text-cyan-400',
                arrow: 'bg-cyan-500/10 text-cyan-400',
            }
        },
        {
            icon: <CreditCard className="w-5 h-5" />,
            title: '결제 페이지 이탈',
            type: '결제 완료',
            problem: '퍼널 상 결제 시작 → 완료에서 이탈이 가장 컸지만 페이지엔 문제가 없어 보였음',
            solution: '이탈 세션 다수가 배송지 입력 후 "우편번호 검색" 팝업에서 멈춤 — 팝업이 화면 밖으로 열리고 있었음',
            result: '결제 완료율 22% 개선',
            classes: {
                glow: 'bg-sky-500/10',
                iconBox: 'text-sky-400 group-hover:bg-sky-600',
                badge: 'bg-sky-900/30 text-sky-400 border-sky-500/20',
                result: 'text-sky-400',
                arrow: 'bg-sky-500/10 text-sky-400',
            }
        },
        {
            icon: <UserPlus className="w-5 h-5" />,
            title: '회원가입 폼 막힘',
            type: '가입 전환',
            problem: '가입 페이지 체류 시간은 긴데 완료율이 낮음 — 어떤 필드가 문제인지 추측만 가능',
            solution: '재생 타임라인의 입력 이벤트를 따라가 보니 비밀번호 규칙 오류 메시지가 안 보여 같은 필드를 반복 수정',
            result: '가입 완료율 31% 상승',
            classes: {
                glow: 'bg-blue-500/10',
                iconBox: 'text-blue-400 group-hover:bg-blue-600',
                badge: 'bg-blue-900/30 text-blue-400 border-blue-500/20',
                result: 'text-blue-400',
                arrow: 'bg-blue-500/10 text-blue-400',
            }
        },
        {
            icon: <Smartphone className="w-5 h-5" />,
            title: '특정 환경에서만 깨지는 화면',
            type: '버그 재현',
            problem: '"화면이 이상해요" 문의가 간헐적으로 들어오지만 개발팀은 재현하지 못함',
            solution: '디바이스·브라우저로 필터링해 해당 세션을 재생 — iOS 인앱 브라우저에서만 하단 버튼이 가려짐을 확인',
            result: '문의 접수 후 재현까지 수일 → 수 분',
            classes: {
                glow: 'bg-violet-500/10',
                iconBox: 'text-violet-400 group-hover:bg-violet-600',
                badge: 'bg-violet-900/30 text-violet-400 border-violet-500/20',
                result: 'text-violet-400',
                arrow: 'bg-violet-500/10 text-violet-400',
            }
        }
    ];

    const timelineIcon = (type) => {
        if (type === 'click') return <MousePointerClick className="w-3 h-3" />;
        if (type === 'scroll') return <ScrollText className="w-3 h-3" />;
        if (type === 'input') return <Keyboard className="w-3 h-3" />;
        return <FileText className="w-3 h-3" />;
    };

    return (
        <div className="bg-white">
            {/* 플로팅 CTA 버튼 */}
            <div className={`fixed bottom-6 left-6 z-50 transition-all duration-300 ${scrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <Link to="/freestart">
                    <button className="flex items-center bg-gradient-to-r from-cyan-600 to-sky-600 text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group">
                        <span className="font-medium">무료로 시작하기</span>
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </button>
                </Link>
            </div>

            <Header />

            {/* ==========================================
          섹션 1: Hero Section
      ========================================== */}
            <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-[#0B0F19]">
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:40px_40px]"></div>
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-cyan-600/10 rounded-full blur-[120px] pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-sky-600/10 rounded-full blur-[100px] pointer-events-none"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">

                        {/* Left: 텍스트 */}
                        <div className="max-w-2xl">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-semibold mb-8">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                                </span>
                                <span>Session Replay</span>
                            </div>

                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
                                고객이 떠난 이유를 <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-sky-400 to-blue-500">직접 눈으로 확인하세요</span>
                            </h1>

                            <p className="text-base sm:text-xl text-gray-400 mb-10 leading-relaxed max-w-lg">
                                숫자는 '얼마나' 이탈했는지만 말해줍니다.
                                실제 방문자의 화면을 그대로 재생해서, 어디서 헤매고 무엇에 막혀 떠났는지
                                한 명 한 명의 맥락을 확인하세요.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/freestart">
                                    <button className="px-8 py-4 bg-gradient-to-r from-cyan-600 to-sky-600 hover:from-cyan-500 hover:to-sky-500 text-white rounded-xl font-bold text-lg shadow-lg shadow-cyan-900/20 hover:shadow-cyan-500/30 transition-all hover:-translate-y-1">
                                        무료로 세션 녹화 시작하기
                                    </button>
                                </Link>
                                <Link to="/service/heatmap">
                                    <button className="px-8 py-4 bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-xl font-medium text-lg transition-all backdrop-blur-sm flex items-center justify-center gap-2 group">
                                        히트맵 분석 보기
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </Link>
                            </div>

                            <div className="mt-8 flex items-center gap-4 text-sm text-gray-500">
                                <div className="flex -space-x-2">
                                    {[1, 2, 3, 4].map(i => (
                                        <div key={i} className="w-8 h-8 rounded-full bg-gray-800 border-2 border-[#0B0F19] flex items-center justify-center text-xs font-bold text-gray-400">
                                            U{i}
                                        </div>
                                    ))}
                                </div>
                                <p>입력값은 기본 마스킹 — 개인정보 걱정 없이 도입</p>
                            </div>
                        </div>

                        {/* Right: 3D 리플레이 플레이어 비주얼 */}
                        <div className="relative perspective-1000 hidden lg:block">
                            <div className="relative transform rotate-y-[-12deg] rotate-x-[5deg] transition-transform duration-700 hover:rotate-y-[-5deg] hover:rotate-x-[2deg]">
                                <div className="bg-[#1A1F2E] border border-gray-700/50 rounded-2xl shadow-2xl overflow-hidden min-h-[500px] relative">

                                    {/* Browser Header */}
                                    <div className="bg-[#0F131C] px-4 py-3 flex items-center gap-2 border-b border-gray-800">
                                        <div className="flex gap-1.5">
                                            <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                                            <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                                            <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                                        </div>
                                        <div className="ml-4 bg-[#1A1F2E] px-3 py-1 rounded text-[10px] text-gray-500 font-mono flex-1 text-center">
                                            switchup.com/replay/sessions/2841
                                        </div>
                                        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-red-500/10 border border-red-500/20">
                                            <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></div>
                                            <span className="text-[9px] font-bold text-red-400 tracking-wider">REC</span>
                                        </div>
                                    </div>

                                    {/* 플레이어 상단 바 */}
                                    <div className="px-5 py-3 flex items-center justify-between border-b border-gray-800">
                                        <div className="flex items-center gap-3">
                                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500 to-sky-600 flex items-center justify-center text-[10px] font-bold text-white">수</div>
                                            <div>
                                                <div className="text-xs font-bold text-gray-200">차분한 수달 <span className="text-gray-500 font-mono font-normal">· m9x7</span></div>
                                                <div className="text-[10px] text-gray-500">모바일 · Safari · iOS · 네이버 검색 유입</div>
                                            </div>
                                        </div>
                                        <div className="text-[10px] text-gray-500 font-mono">페이지 2/3</div>
                                    </div>

                                    {/* 재생 스테이지: 방문자 화면 재현 */}
                                    <div className="relative mx-5 mt-4 h-[260px] bg-white rounded-lg overflow-hidden border border-gray-700">
                                        {/* 쇼핑몰 상품 페이지 재현 */}
                                        <div className="h-7 bg-white border-b border-gray-200 flex items-center px-3 gap-3">
                                            <div className="flex items-center gap-1">
                                                <div className="w-3.5 h-3.5 rounded bg-cyan-500"></div>
                                                <span className="text-[8px] font-black text-gray-900 tracking-tight">SWITCH SHOP</span>
                                            </div>
                                            <div className="flex gap-2 text-[7px] text-gray-500 font-medium">
                                                <span>NEW</span><span className="text-gray-900 font-bold">BEST</span><span>아우터</span><span>상의</span><span>하의</span>
                                            </div>
                                            <div className="ml-auto flex items-center gap-2 text-gray-400">
                                                <Search className="w-2.5 h-2.5" />
                                                <div className="relative">
                                                    <ShoppingCart className="w-2.5 h-2.5" />
                                                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-cyan-500 text-white text-[5px] font-bold flex items-center justify-center">1</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="p-3 grid grid-cols-5 gap-3">
                                            {/* 상품 이미지 */}
                                            <div className="col-span-2 h-28 rounded-lg bg-gradient-to-br from-slate-100 via-slate-200 to-slate-300 relative overflow-hidden">
                                                <span className="absolute top-1.5 left-1.5 px-1 py-0.5 rounded bg-gray-900 text-white text-[6px] font-bold">BEST</span>
                                                <div className="absolute left-5 right-5 top-7 bottom-2 rounded-t-[40%] bg-slate-400/40"></div>
                                                <div className="absolute left-1/2 -translate-x-1/2 top-9 w-3 h-8 bg-slate-300/80 rounded-b"></div>
                                            </div>
                                            {/* 상품 정보 */}
                                            <div className="col-span-3 pt-0.5">
                                                <div className="text-[7px] text-gray-400">상의 · 셔츠</div>
                                                <div className="text-[9px] font-bold text-gray-900 leading-tight mt-0.5">코튼 오버핏 셔츠 (3 colors)</div>
                                                <div className="flex items-baseline gap-1 mt-1">
                                                    <span className="text-[7px] text-gray-400 line-through">52,000원</span>
                                                    <span className="text-[10px] font-black text-gray-900">39,000원</span>
                                                    <span className="text-[7px] font-bold text-red-500">25%</span>
                                                </div>
                                                {/* 색상 옵션 (반복 클릭 대상) */}
                                                <div className="mt-2 h-6 rounded border border-red-300 bg-red-50/60 flex items-center justify-between px-2 hero-shake">
                                                    <span className="text-[7px] text-gray-600">색상 선택</span>
                                                    <ChevronRight className="w-3 h-3 text-gray-400 rotate-90" />
                                                </div>
                                                <div className="mt-1 h-6 rounded border border-gray-200 bg-white flex items-center justify-between px-2">
                                                    <span className="text-[7px] text-gray-400">사이즈 선택</span>
                                                    <ChevronRight className="w-3 h-3 text-gray-300 rotate-90" />
                                                </div>
                                                <div className="mt-1.5 flex gap-1">
                                                    <div className="flex-1 h-6 rounded bg-gray-900 text-white text-[7px] font-bold flex items-center justify-center">장바구니 담기</div>
                                                    <div className="flex-1 h-6 rounded bg-cyan-600 text-white text-[7px] font-bold flex items-center justify-center">바로 구매</div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* 추천 상품 */}
                                        <div className="px-3">
                                            <div className="text-[7px] font-bold text-gray-700 mb-1">함께 보면 좋은 상품</div>
                                            <div className="grid grid-cols-3 gap-2">
                                                {[
                                                    { name: '린넨 와이드 팬츠', price: '45,000' },
                                                    { name: '베이직 크루넥 티', price: '19,000' },
                                                    { name: '캔버스 토트백', price: '29,000' },
                                                ].map((item, i) => (
                                                    <div key={i} className="rounded-lg border border-gray-200 overflow-hidden">
                                                        <div className="h-8 bg-gradient-to-br from-gray-100 to-gray-200"></div>
                                                        <div className="px-1 py-0.5">
                                                            <div className="text-[6px] text-gray-600 truncate">{item.name}</div>
                                                            <div className="text-[7px] font-bold text-gray-900">{item.price}원</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* 커서 궤적 (CSS 애니메이션) */}
                                        <div className="absolute inset-0 pointer-events-none">
                                            <div className="absolute hero-cursor">
                                                <MousePointer2 className="w-4 h-4 text-gray-900 fill-white drop-shadow" />
                                            </div>
                                            <div className="absolute hero-ripple w-5 h-5 rounded-full border-2 border-cyan-500"></div>
                                            <div className="absolute hero-ripple hero-ripple-2 w-5 h-5 rounded-full border-2 border-cyan-500"></div>
                                            <div className="absolute hero-ripple hero-ripple-3 w-5 h-5 rounded-full border-2 border-cyan-500"></div>
                                        </div>
                                    </div>

                                    {/* 타임라인 + 컨트롤 */}
                                    <div className="px-5 pt-4 pb-5">
                                        <div className="relative h-2 bg-gray-800 rounded-full overflow-visible">
                                            <div className="absolute left-[46%] w-[16%] h-full bg-gray-700/70 rounded-full"></div>
                                            <div className="absolute left-0 h-full bg-gradient-to-r from-cyan-500 to-sky-500 rounded-full hero-progress"></div>
                                            {/* 이벤트 마커 */}
                                            {[
                                                { left: '18%', color: 'bg-cyan-400' },
                                                { left: '26%', color: 'bg-cyan-400' },
                                                { left: '31%', color: 'bg-cyan-400' },
                                                { left: '40%', color: 'bg-sky-300' },
                                                { left: '78%', color: 'bg-amber-400' },
                                                { left: '90%', color: 'bg-cyan-400' },
                                            ].map((m, i) => (
                                                <div key={i} className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2 h-2 rounded-full ${m.color} ring-2 ring-[#1A1F2E]`} style={{ left: m.left }}></div>
                                            ))}
                                            <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-white shadow ring-2 ring-cyan-500 hero-knob"></div>
                                        </div>
                                        <div className="mt-3 flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                                                    <Pause className="w-3.5 h-3.5 text-gray-900 fill-gray-900" />
                                                </div>
                                                <span className="text-xs font-mono text-gray-300">0:31 <span className="text-gray-600">/ 1:52</span></span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="px-2 py-0.5 rounded bg-gray-800 text-[10px] font-bold text-gray-300">2x</span>
                                                <span className="px-2 py-0.5 rounded bg-cyan-500/15 text-[10px] font-bold text-cyan-400 flex items-center gap-1">
                                                    <SkipForward className="w-3 h-3" /> 비활동 건너뛰기
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Floating Tooltip */}
                                    <div className="absolute top-[38%] -right-2 bg-gray-900/90 backdrop-blur border border-red-500/30 text-white px-4 py-2 rounded-lg shadow-xl z-20 animate-bounce-slow">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
                                            <span className="text-xs font-bold text-red-400 uppercase">이탈 직전</span>
                                        </div>
                                        <div className="text-sm font-bold">옵션 선택 3회 반복</div>
                                        <div className="text-xs text-gray-400 mt-0.5">드롭다운이 열리지 않음</div>
                                    </div>
                                </div>

                                <div className="absolute -bottom-10 left-10 right-10 h-20 bg-cyan-500/20 blur-[60px] rounded-full transform scale-y-50"></div>
                            </div>
                        </div>

                    </div>
                </div>

                <style>{`
          .perspective-1000 { perspective: 1000px; }
          .rotate-y-\\[-12deg\\] { transform: rotateY(-12deg) rotateX(5deg); }
          @keyframes bounce-slow {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
          .animate-bounce-slow { animation: bounce-slow 3s ease-in-out infinite; }

          /* 히어로 커서 궤적: 우상단 → 옵션 드롭다운(3회 클릭) → 하단 */
          @keyframes hero-cursor-path {
            0%   { left: 82%; top: 6%; }
            18%  { left: 64%; top: 33%; }
            24%  { left: 65%; top: 35%; }
            30%  { left: 64%; top: 33%; }
            36%  { left: 65%; top: 35%; }
            42%  { left: 64%; top: 33%; }
            60%  { left: 38%; top: 86%; }
            80%  { left: 38%; top: 86%; }
            100% { left: 82%; top: 6%; }
          }
          .hero-cursor { animation: hero-cursor-path 9s ease-in-out infinite; }

          @keyframes hero-ripple {
            0%, 17%  { opacity: 0; transform: translate(-50%, -50%) scale(0.3); }
            20%      { opacity: 0.9; transform: translate(-50%, -50%) scale(0.4); }
            30%      { opacity: 0; transform: translate(-50%, -50%) scale(1.8); }
            100%     { opacity: 0; }
          }
          .hero-ripple { left: 64%; top: 33%; animation: hero-ripple 9s ease-out infinite; }
          .hero-ripple-2 { animation-delay: 1.1s; }
          .hero-ripple-3 { animation-delay: 2.2s; }

          @keyframes hero-shake {
            0%, 16%, 44%, 100% { transform: translateX(0); }
            19%, 31%, 43% { transform: translateX(-1.5px); }
            25%, 37% { transform: translateX(1.5px); }
          }
          .hero-shake { animation: hero-shake 9s ease-in-out infinite; }

          @keyframes hero-progress {
            0% { width: 0%; }
            100% { width: 100%; }
          }
          .hero-progress { animation: hero-progress 9s linear infinite; }
          @keyframes hero-knob {
            0% { left: 0%; }
            100% { left: 100%; }
          }
          .hero-knob { animation: hero-knob 9s linear infinite; }

          @media (prefers-reduced-motion: reduce) {
            .hero-cursor, .hero-ripple, .hero-shake, .hero-progress, .hero-knob, .animate-bounce-slow { animation: none; }
          }
        `}</style>
            </section>

            {/* ==========================================
          섹션 2: Why Replay? — 집계 데이터 vs 세션 녹화
      ========================================== */}
            <section className="py-24 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-block px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                            Why Session Replay?
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                            평균값에는 <br className="sm:hidden" />
                            <span className="text-cyan-600">'한 사람'</span>이 없습니다
                        </h2>
                        <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
                            퍼널과 히트맵은 수천 명을 합친 경향을 보여줍니다.
                            세션 녹화는 그 경향 뒤에 숨은 개별 방문자의 실제 경험을 보여줍니다.
                        </p>
                    </div>

                    <div className="relative max-w-5xl mx-auto">
                        <div className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white rounded-full items-center justify-center shadow-lg z-10 font-black text-gray-300 italic text-xl border-4 border-gray-50">
                            VS
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 md:gap-0">

                            {/* 집계 데이터 */}
                            <div className="bg-white p-8 md:p-12 rounded-2xl md:rounded-r-none md:rounded-l-2xl border border-gray-200 shadow-sm relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-gray-100 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                                <div className="relative z-10">
                                    <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-6 text-gray-400">
                                        <BarChart3 className="w-8 h-8" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-500 mb-2">집계 데이터</h3>
                                    <p className="text-gray-400 text-sm mb-8 font-medium">Aggregated Numbers</p>

                                    <ul className="space-y-6">
                                        {[
                                            { text: "옵션 영역 클릭률 18%", sub: "많이 누른 건데, 왜 전환이 안 되죠?" },
                                            { text: "결제 페이지 평균 체류 2분 40초", sub: "오래 머문 게 좋은 건가요, 헤맨 건가요?" },
                                            { text: "모바일 이탈률 PC의 2배", sub: "모바일에서 도대체 무슨 일이 있는 거죠?" }
                                        ].map((item, idx) => (
                                            <li key={idx} className="flex items-start gap-4 opacity-70 group-hover:opacity-100 transition-opacity">
                                                <div className="mt-1 min-w-[24px]">
                                                    <HelpCircle className="w-6 h-6 text-gray-300" />
                                                </div>
                                                <div>
                                                    <div className="text-gray-600 font-medium text-lg">"{item.text}"</div>
                                                    <div className="text-gray-400 text-xs mt-0.5">{item.sub}</div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            {/* 세션 녹화 */}
                            <div className="bg-[#1A1F2E] p-8 md:p-12 rounded-2xl md:rounded-l-none md:rounded-r-2xl border border-gray-700 shadow-2xl relative overflow-hidden transform md:scale-105 md:-my-4 z-0 ring-1 ring-cyan-500/20">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-cyan-500/20 to-sky-500/20 rounded-full blur-3xl opacity-60 -mr-20 -mt-20 animate-pulse"></div>
                                <div className="relative z-10">
                                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-sky-600 rounded-2xl flex items-center justify-center mb-6 text-white shadow-lg shadow-cyan-500/30">
                                        <Video className="w-8 h-8" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-white mb-2">세션 녹화</h3>
                                    <p className="text-cyan-400 text-sm mb-8 font-bold tracking-wide">One Visitor, Full Context</p>

                                    <ul className="space-y-6">
                                        {[
                                            { text: "드롭다운이 안 열려 3번 두드리고 이탈", sub: "클릭률이 높았던 진짜 이유" },
                                            { text: "우편번호 팝업이 화면 밖에서 열림", sub: "2분 40초는 헤맨 시간이었음" },
                                            { text: "인앱 브라우저에서 구매 버튼이 가려짐", sub: "모바일 이탈의 구체적 장면" }
                                        ].map((item, idx) => (
                                            <li key={idx} className="flex items-start gap-4">
                                                <div className="mt-1 min-w-[24px]">
                                                    <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                                                        <Check className="w-4 h-4 text-green-400 stroke-[3]" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-white font-bold text-lg">{item.text}</div>
                                                    <div className="text-gray-400 text-xs mt-0.5 font-medium">{item.sub}</div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className="mt-10 pt-8 border-t border-gray-700">
                                        <div className="flex items-center gap-2 text-cyan-400 font-bold">
                                            <span>추측 대신 장면으로 판단하세요</span>
                                            <ArrowRight className="w-5 h-5" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 3: 재생기 핵심 기능 4가지 카드
      ========================================== */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                            보고 싶은 장면까지 빠르게
                        </h2>
                        <p className="text-xl text-gray-600">
                            수천 개 세션 중 지금 봐야 할 세션을 고르고, <br className="sm:hidden" />필요한 순간으로 바로 이동합니다
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">

                        {/* 1. 화면 그대로 재생 */}
                        <div className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:border-cyan-200 transition-all duration-300">
                            <div className="h-36 bg-gray-50 rounded-xl mb-6 relative overflow-hidden border border-gray-100 group-hover:bg-cyan-50/30 transition-colors p-3">
                                <div className="h-full bg-white rounded-lg border border-gray-200 relative overflow-hidden">
                                    <div className="h-4 bg-gray-100 border-b border-gray-200"></div>
                                    <div className="p-2 space-y-1.5">
                                        <div className="w-2/3 h-1.5 rounded bg-gray-300"></div>
                                        <div className="w-1/2 h-1.5 rounded bg-gray-200"></div>
                                        <div className="w-1/3 h-4 rounded bg-cyan-500 mt-2"></div>
                                    </div>
                                    <div className="absolute card-cursor">
                                        <MousePointer2 className="w-3.5 h-3.5 text-gray-900 fill-white" />
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-cyan-100 text-cyan-600 rounded-lg">
                                    <Play className="w-5 h-5" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">화면 그대로 재생</h3>
                            </div>
                            <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                                마우스 이동, 클릭, 스크롤, 입력, 화면 변화를 방문자가 본 그대로 재현합니다.
                            </p>
                            <ul className="space-y-1.5">
                                {['PC · 모바일 터치 모두 지원', '페이지 이동도 한 세션으로 이어서'].map((feat, i) => (
                                    <li key={i} className="flex items-center text-xs text-gray-500 font-medium">
                                        <CheckCircle2 className="w-3 h-3 text-cyan-500 mr-2" /> {feat}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* 2. 이벤트 타임라인 */}
                        <div className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:border-sky-200 transition-all duration-300">
                            <div className="h-36 bg-gray-50 rounded-xl mb-6 relative overflow-hidden border border-gray-100 group-hover:bg-sky-50/30 transition-colors p-4 flex flex-col justify-center gap-2">
                                {[
                                    { t: '0:04', label: '클릭 · 카테고리 메뉴', icon: <MousePointerClick className="w-3 h-3" />, active: false },
                                    { t: '0:11', label: '페이지 이동 · /product/128', icon: <FileText className="w-3 h-3" />, active: false },
                                    { t: '0:27', label: '입력 · 수량', icon: <Keyboard className="w-3 h-3" />, active: true },
                                    { t: '0:31', label: '클릭 · 장바구니 담기', icon: <MousePointerClick className="w-3 h-3" />, active: false },
                                ].map((ev, i) => (
                                    <div key={i} className={`flex items-center gap-2 px-2 py-1 rounded-md text-[10px] ${ev.active ? 'bg-sky-100 text-sky-700 font-bold' : 'text-gray-500'}`}>
                                        <span className="font-mono w-7">{ev.t}</span>
                                        <span className={ev.active ? 'text-sky-600' : 'text-gray-400'}>{ev.icon}</span>
                                        <span className="truncate">{ev.label}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-sky-100 text-sky-600 rounded-lg">
                                    <ScrollText className="w-5 h-5" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">이벤트 타임라인</h3>
                            </div>
                            <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                                클릭·입력·페이지 이동을 시간순으로 나열하고, 항목을 누르면 그 시점으로 이동합니다.
                            </p>
                            <ul className="space-y-1.5">
                                {['클릭한 요소 이름 표시', '페이지별 필터'].map((feat, i) => (
                                    <li key={i} className="flex items-center text-xs text-gray-500 font-medium">
                                        <CheckCircle2 className="w-3 h-3 text-sky-500 mr-2" /> {feat}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* 3. 비활동 건너뛰기 + 배속 */}
                        <div className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:border-amber-200 transition-all duration-300">
                            <div className="h-36 bg-gray-900 rounded-xl mb-6 relative overflow-hidden flex flex-col items-center justify-center p-5 border border-gray-100 group-hover:border-amber-200 transition-colors">
                                <div className="w-full h-2 bg-gray-800 rounded-full relative overflow-hidden">
                                    <div className="absolute left-0 w-[30%] h-full bg-cyan-500 rounded-l-full"></div>
                                    <div className="absolute left-[30%] w-[40%] h-full bg-gray-700 skip-stripes"></div>
                                    <div className="absolute left-[70%] w-[30%] h-full bg-cyan-500 rounded-r-full"></div>
                                </div>
                                <div className="w-full flex justify-between mt-2 text-[9px] font-mono text-gray-500">
                                    <span>0:00</span>
                                    <span className="text-amber-400 font-bold">+ 2분 10초 건너뜀</span>
                                    <span>4:32</span>
                                </div>
                                <div className="mt-4 flex items-center gap-2">
                                    {['1x', '2x', '4x'].map((s, i) => (
                                        <span key={s} className={`px-2 py-0.5 rounded text-[10px] font-bold ${i === 1 ? 'bg-amber-400 text-gray-900' : 'bg-gray-800 text-gray-400'}`}>{s}</span>
                                    ))}
                                </div>
                            </div>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-amber-100 text-amber-600 rounded-lg">
                                    <FastForward className="w-5 h-5" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">비활동 건너뛰기</h3>
                            </div>
                            <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                                방문자가 가만히 있던 구간은 자동으로 건너뛰고, 배속으로 핵심만 빠르게 훑습니다.
                            </p>
                            <ul className="space-y-1.5">
                                {['5초 이상 무조작 구간 자동 점프', '1x · 2x · 4x 배속'].map((feat, i) => (
                                    <li key={i} className="flex items-center text-xs text-gray-500 font-medium">
                                        <CheckCircle2 className="w-3 h-3 text-amber-500 mr-2" /> {feat}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* 4. 세션 필터 */}
                        <div className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl hover:border-blue-200 transition-all duration-300">
                            <div className="h-36 bg-gray-50 rounded-xl mb-6 relative overflow-hidden border border-gray-100 group-hover:bg-blue-50/30 transition-colors p-4 flex flex-col justify-center gap-2">
                                <div className="flex flex-wrap gap-1.5">
                                    {[
                                        { label: '모바일', on: true, icon: <Smartphone className="w-2.5 h-2.5" /> },
                                        { label: '30초 이상', on: true, icon: <Clock className="w-2.5 h-2.5" /> },
                                        { label: '즐겨찾기', on: false, icon: <Star className="w-2.5 h-2.5" /> },
                                        { label: '/checkout', on: true, icon: <Search className="w-2.5 h-2.5" /> },
                                    ].map((f, i) => (
                                        <span key={i} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border ${f.on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-400 border-gray-200'}`}>
                                            {f.icon}{f.label}
                                        </span>
                                    ))}
                                </div>
                                <div className="mt-2 text-[10px] text-gray-500">
                                    <span className="font-bold text-gray-900 text-base">38</span> / 2,410 세션
                                </div>
                            </div>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                                    <Filter className="w-5 h-5" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">세션 필터</h3>
                            </div>
                            <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                                디바이스, 세션 길이, 방문 URL, 즐겨찾기로 봐야 할 세션만 추려냅니다.
                            </p>
                            <ul className="space-y-1.5">
                                {['긴 세션 · 특정 페이지 방문자 우선', '중요한 세션은 별표로 표시'].map((feat, i) => (
                                    <li key={i} className="flex items-center text-xs text-gray-500 font-medium">
                                        <CheckCircle2 className="w-3 h-3 text-blue-500 mr-2" /> {feat}
                                    </li>
                                ))}
                            </ul>
                        </div>

                    </div>
                </div>

                <style>{`
          @keyframes card-cursor-path {
            0%   { left: 70%; top: 20%; }
            40%  { left: 22%; top: 68%; }
            55%  { left: 24%; top: 70%; }
            100% { left: 70%; top: 20%; }
          }
          .card-cursor { animation: card-cursor-path 5s ease-in-out infinite; }
          .skip-stripes {
            background-image: repeating-linear-gradient(45deg, #374151 0 4px, #1f2937 4px 8px);
          }
          @media (prefers-reduced-motion: reduce) { .card-cursor { animation: none; } }
        `}</style>
            </section>

            {/* ==========================================
          섹션 4: 인터랙티브 플레이어 체험
      ========================================== */}
            <section className="py-24 bg-gray-50 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row items-center gap-16">

                        {/* Left: 설명 */}
                        <div className="flex-1 max-w-xl">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-semibold mb-6">
                                <Play className="w-4 h-4" />
                                <span>Try the Player</span>
                            </div>

                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                                직접 재생해 보세요<br />
                                <span className="text-cyan-600">장바구니까지 48초</span>
                            </h2>

                            <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                                오른쪽은 실제 재생 화면을 축소한 데모입니다.
                                재생을 누르면 방문자의 커서가 움직이고, 타임라인의 이벤트가 순서대로 켜집니다.
                                비활동 건너뛰기를 끄면 18초의 공백을 그대로 기다리게 됩니다.
                            </p>

                            <div className="space-y-6 mb-8">
                                {[
                                    { title: "같은 요소를 반복해서 클릭", desc: "0:09와 0:14에 색상 선택을 두 번 누릅니다. 타임라인만 봐도 막힌 지점이 보입니다.", icon: <MousePointerClick className="w-5 h-5 text-cyan-600" /> },
                                    { title: "공백은 자동으로 건너뜀", desc: "0:25~0:34 사이 아무 조작이 없습니다. 회색 띠 구간을 점프해 기다리지 않습니다.", icon: <SkipForward className="w-5 h-5 text-cyan-600" /> },
                                    { title: "타임라인에서 바로 이동", desc: "이벤트를 누르면 그 순간으로 점프합니다. 긴 세션도 보고 싶은 곳만 봅니다.", icon: <Route className="w-5 h-5 text-cyan-600" /> }
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4">
                                        <div className="mt-1 w-10 h-10 bg-cyan-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                            {item.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                                            <p className="text-sm text-gray-600">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right: 인터랙티브 플레이어 */}
                        <div className="flex-1 w-full relative">
                            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden relative select-none">
                                {/* 헤더 */}
                                <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-cyan-500 to-sky-600 flex items-center justify-center text-[9px] font-bold text-white">고</div>
                                        <span className="text-xs font-bold text-gray-200">고요한 여우 <span className="text-gray-500 font-mono font-normal">· k2p8</span></span>
                                    </div>
                                    <span className="text-[10px] text-gray-500 font-mono">/product/128</span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-5">
                                    {/* 스테이지 */}
                                    <div className="sm:col-span-3 p-4 border-b sm:border-b-0 sm:border-r border-gray-100">
                                        <div
                                            className="relative aspect-[4/3] bg-gray-50 rounded-lg border border-gray-200 overflow-hidden cursor-pointer"
                                            onClick={togglePlay}
                                            role="button"
                                            aria-label={playing ? '일시정지' : '재생'}
                                        >
                                            {/* 쇼핑몰 상품 페이지 재현 */}
                                            <div className="h-[9%] bg-white border-b border-gray-200 flex items-center px-2 gap-2">
                                                <div className="flex items-center gap-1">
                                                    <div className="w-3 h-3 rounded bg-cyan-500"></div>
                                                    <span className="text-[7px] font-black text-gray-900">SWITCH SHOP</span>
                                                </div>
                                                <div className="flex gap-1.5 text-[6px] text-gray-400">
                                                    <span>NEW</span><span>BEST</span><span>상의</span><span>하의</span>
                                                </div>
                                                <ShoppingCart className="ml-auto w-2.5 h-2.5 text-gray-400" />
                                            </div>
                                            <div className="p-2.5 grid grid-cols-5 gap-2">
                                                {/* 상품 이미지 */}
                                                <div className="col-span-2 h-24 rounded-md bg-gradient-to-br from-slate-100 via-slate-200 to-slate-300 relative overflow-hidden">
                                                    <span className="absolute top-1 left-1 px-1 rounded bg-gray-900 text-white text-[5px] font-bold">BEST</span>
                                                    <div className="absolute left-4 right-4 top-6 bottom-2 rounded-t-[40%] bg-slate-400/40"></div>
                                                </div>
                                                {/* 상품 정보 */}
                                                <div className="col-span-3">
                                                    <div className="text-[6px] text-gray-400">상의 · 셔츠</div>
                                                    <div className="text-[8px] font-bold text-gray-900 leading-tight">코튼 오버핏 셔츠</div>
                                                    <div className="flex items-baseline gap-1 mt-0.5">
                                                        <span className="text-[6px] text-gray-400 line-through">52,000</span>
                                                        <span className="text-[9px] font-black text-gray-900">39,000원</span>
                                                        <span className="text-[6px] font-bold text-red-500">25%</span>
                                                    </div>
                                                    {/* 색상 옵션 (반복 클릭 대상) */}
                                                    <div className={`mt-1.5 h-5 rounded border flex items-center justify-between px-1.5 transition-colors ${progress >= 18 && progress < 36 ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'}`}>
                                                        <span className="text-[6px] text-gray-600">색상 선택</span>
                                                        <ChevronRight className="w-2.5 h-2.5 text-gray-400 rotate-90" />
                                                    </div>
                                                    <div className="mt-1 h-5 rounded border border-gray-200 bg-white flex items-center justify-between px-1.5">
                                                        <span className="text-[6px] text-gray-400">사이즈 선택</span>
                                                        <ChevronRight className="w-2.5 h-2.5 text-gray-300 rotate-90" />
                                                    </div>
                                                </div>
                                            </div>
                                            {/* 스크롤 후 노출되는 하단 영역 */}
                                            <div className={`px-2.5 transition-transform duration-700 ${progress >= 40 ? 'translate-y-0' : 'translate-y-6'}`}>
                                                <div className="text-[6px] font-bold text-gray-700 mb-1">함께 보면 좋은 상품</div>
                                                <div className="grid grid-cols-3 gap-1.5 mb-2">
                                                    {['린넨 팬츠', '크루넥 티', '토트백'].map((n, i) => (
                                                        <div key={i} className="rounded border border-gray-200 overflow-hidden bg-white">
                                                            <div className="h-4 bg-gradient-to-br from-gray-100 to-gray-200"></div>
                                                            <div className="px-1 text-[5px] text-gray-600 truncate">{n}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {/* 수량 입력 */}
                                                    <div className={`flex-1 h-5 rounded border flex items-center justify-between px-1.5 transition-colors bg-white ${progress >= 78 && progress < 90 ? 'border-cyan-400 ring-2 ring-cyan-100' : 'border-gray-300'}`}>
                                                        <span className="text-[8px] text-gray-400">−</span>
                                                        <span className="text-[8px] font-mono font-bold text-gray-800">
                                                            {progress >= 78 ? '2' : '1'}<span className={progress >= 78 && progress < 90 ? 'animate-pulse text-cyan-500' : 'opacity-0'}>|</span>
                                                        </span>
                                                        <span className="text-[8px] text-gray-400">+</span>
                                                    </div>
                                                    {/* 장바구니 버튼 */}
                                                    <div className={`w-1/2 h-5 rounded flex items-center justify-center text-[7px] font-bold text-white transition-colors ${progress >= 90 ? 'bg-cyan-600' : 'bg-gray-900'}`}>
                                                        {progress >= 90 ? '담았습니다 ✓' : '장바구니 담기'}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* 페이지 이동 오버레이 */}
                                            {progress >= 100 && (
                                                <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
                                                    <div className="text-xs font-mono text-gray-500">→ /cart</div>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleRestart(); }}
                                                        className="w-12 h-12 rounded-full bg-cyan-600 text-white flex items-center justify-center shadow-lg hover:bg-cyan-500 transition-colors"
                                                        aria-label="다시 재생"
                                                    >
                                                        <Play className="w-5 h-5 fill-white ml-0.5" />
                                                    </button>
                                                    <span className="text-[10px] text-gray-500">다시 재생</span>
                                                </div>
                                            )}

                                            {/* 초기 안내 */}
                                            {progress === 0 && !playing && (
                                                <div className="absolute inset-0 bg-gray-900/40 flex items-center justify-center">
                                                    <div className="w-14 h-14 rounded-full bg-white shadow-xl flex items-center justify-center">
                                                        <Play className="w-6 h-6 text-gray-900 fill-gray-900 ml-0.5" />
                                                    </div>
                                                </div>
                                            )}

                                            {/* 커서 + 클릭 리플 */}
                                            {progress > 0 && progress < 100 && (
                                                <div
                                                    className="absolute pointer-events-none transition-all duration-100 ease-linear z-10"
                                                    style={{ left: `${cursor.x}%`, top: `${cursor.y}%` }}
                                                >
                                                    <MousePointer2 className="w-4 h-4 text-gray-900 fill-white drop-shadow" />
                                                </div>
                                            )}
                                            {clickPulse && progress > 0 && progress < 100 && (
                                                <div
                                                    key={clickPulse.key}
                                                    className="absolute w-6 h-6 rounded-full border-2 border-cyan-500 pointer-events-none demo-ripple z-10"
                                                    style={{ left: `${clickPulse.x}%`, top: `${clickPulse.y}%` }}
                                                ></div>
                                            )}
                                        </div>

                                        {/* 진행 바 */}
                                        <div className="mt-3">
                                            <div
                                                className="relative h-2 bg-gray-200 rounded-full cursor-pointer"
                                                onClick={(e) => {
                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
                                                    setProgress(ratio * 100);
                                                    lastEventRef.current = -1;
                                                }}
                                            >
                                                <div
                                                    className="absolute h-full bg-gray-300 rounded-full"
                                                    style={{ left: `${DEMO_INACTIVE.start}%`, width: `${DEMO_INACTIVE.end - DEMO_INACTIVE.start}%` }}
                                                    title="비활동 구간"
                                                ></div>
                                                <div className="absolute left-0 h-full bg-gradient-to-r from-cyan-500 to-sky-500 rounded-full" style={{ width: `${progress}%` }}></div>
                                                {demoEvents.map((ev, i) => (
                                                    <div key={i} className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2 h-2 rounded-full ring-2 ring-white ${progress >= ev.t ? 'bg-cyan-600' : 'bg-gray-400'}`} style={{ left: `${ev.t}%` }}></div>
                                                ))}
                                                <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-white shadow ring-2 ring-cyan-500" style={{ left: `${progress}%` }}></div>
                                            </div>

                                            <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                                                <div className="flex items-center gap-3">
                                                    <button
                                                        onClick={togglePlay}
                                                        className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center hover:bg-cyan-600 transition-colors"
                                                        aria-label={playing ? '일시정지' : '재생'}
                                                    >
                                                        {playing ? <Pause className="w-3.5 h-3.5 fill-white" /> : <Play className="w-3.5 h-3.5 fill-white ml-0.5" />}
                                                    </button>
                                                    <span className="text-xs font-mono text-gray-700">{formatClock(progress)} <span className="text-gray-400">/ 0:{DEMO_DURATION}</span></span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex rounded-md overflow-hidden border border-gray-200">
                                                        {[1, 2, 4].map(s => (
                                                            <button
                                                                key={s}
                                                                onClick={() => setSpeed(s)}
                                                                className={`px-2 py-1 text-[10px] font-bold transition-colors ${speed === s ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                                                            >
                                                                {s}x
                                                            </button>
                                                        ))}
                                                    </div>
                                                    <button
                                                        onClick={() => setSkipInactive(v => !v)}
                                                        className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold border transition-colors ${skipInactive ? 'bg-cyan-50 text-cyan-700 border-cyan-200' : 'bg-white text-gray-400 border-gray-200'}`}
                                                    >
                                                        <SkipForward className="w-3 h-3" />
                                                        건너뛰기 {skipInactive ? 'ON' : 'OFF'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 타임라인 */}
                                    <div className="sm:col-span-2 p-4 bg-gray-50/60">
                                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">타임라인</div>
                                        <ul className="space-y-1.5">
                                            {demoEvents.map((ev, i) => {
                                                const passed = progress >= ev.t;
                                                const current = progress >= ev.t && progress < ev.t + 8;
                                                return (
                                                    <li key={i}>
                                                        <button
                                                            onClick={() => { setProgress(ev.t); lastEventRef.current = -1; setPlaying(true); }}
                                                            className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left text-xs transition-all border ${current ? 'bg-cyan-600 text-white border-cyan-600 shadow-md' : passed ? 'bg-white text-gray-800 border-gray-200' : 'bg-transparent text-gray-400 border-transparent'}`}
                                                        >
                                                            <span className={`font-mono text-[10px] w-8 ${current ? 'text-cyan-100' : 'text-gray-400'}`}>{formatClock(ev.t)}</span>
                                                            <span className={current ? 'text-white' : passed ? 'text-cyan-600' : 'text-gray-300'}>{timelineIcon(ev.event.icon)}</span>
                                                            <span className="truncate font-medium">{ev.event.label}</span>
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                        <div className="mt-4 pt-3 border-t border-gray-200 text-[10px] text-gray-400 leading-relaxed">
                                            데모 세션입니다. 실제 화면에서는 클릭한 요소 이름, 페이지 목록, 방문자 환경 정보가 함께 표시됩니다.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-cyan-100 rounded-full blur-3xl -z-10"></div>
                            <div className="absolute -top-6 -left-6 w-32 h-32 bg-sky-100 rounded-full blur-3xl -z-10"></div>
                        </div>

                    </div>
                </div>

                <style>{`
          @keyframes demo-ripple {
            0%   { opacity: 0.9; transform: translate(-50%, -50%) scale(0.3); }
            100% { opacity: 0; transform: translate(-50%, -50%) scale(2); }
          }
          .demo-ripple { animation: demo-ripple 0.6s ease-out forwards; }
        `}</style>
            </section>

            {/* ==========================================
          섹션 5: 캠페인 설정 — 필요한 곳만 녹화
      ========================================== */}
            <section className="py-24 bg-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row-reverse items-center gap-16">

                        {/* Right: 설명 (반전) */}
                        <div className="flex-1 max-w-xl">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-sm font-semibold mb-6">
                                <Target className="w-4 h-4" />
                                <span>Recording Campaign</span>
                            </div>

                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                                전체가 아니라<br />
                                <span className="text-sky-600">봐야 할 곳만</span> 녹화하세요
                            </h2>

                            <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                                녹화는 캠페인 단위로 켭니다. 결제 페이지만, 특정 상품군만, 모바일 방문자만 —
                                목적별로 캠페인을 나누면 세션이 쌓여도 무엇을 왜 녹화했는지 분명합니다.
                            </p>

                            <div className="space-y-6 mb-8">
                                {[
                                    { title: "URL 규칙으로 대상 지정", desc: "도메인 전체, 특정 경로, '로 시작', '포함' 등 5가지 방식. 제외 URL은 항상 우선 적용됩니다.", icon: <Route className="w-5 h-5 text-sky-600" /> },
                                    { title: "방문자 타게팅", desc: "디바이스·브라우저·유입 경로·신규/재방문 등 A/B 테스트와 같은 조건으로 녹화 대상을 좁힙니다.", icon: <Users className="w-5 h-5 text-sky-600" /> },
                                    { title: "최소 녹화 시간 · 녹화 수 제한", desc: "몇 초 만에 튕긴 세션은 버리고, 캠페인별 보관 세션 수에 상한을 걸어 비용을 통제합니다.", icon: <Timer className="w-5 h-5 text-sky-600" /> }
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4">
                                        <div className="mt-1 w-10 h-10 bg-sky-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                            {item.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                                            <p className="text-sm text-gray-600">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="p-4 bg-gray-50 rounded-xl border border-sky-100 shadow-sm">
                                <h4 className="font-semibold text-gray-900 mb-2 text-sm">설정 팁:</h4>
                                <ul className="space-y-2">
                                    {["퍼널에서 이탈이 큰 페이지부터 캠페인 생성", "마이페이지·주문 내역 등은 제외 URL에 등록", "녹화 수 제한을 걸어두면 80% 도달 시 알려드립니다"].map((text, idx) => (
                                        <li key={idx} className="flex items-center text-sm text-gray-600">
                                            <CheckCircle2 className="w-4 h-4 text-sky-500 mr-2" />
                                            {text}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {/* Left: 설정 위저드 목업 */}
                        <div className="flex-1 w-full relative">
                            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden relative">
                                <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex justify-between items-center">
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">세션 녹화 설정</span>
                                    <div className="flex items-center gap-1 text-[10px] font-bold">
                                        <span className="px-2 py-0.5 rounded-full bg-sky-500 text-white">타겟</span>
                                        <ChevronRight className="w-3 h-3 text-gray-600" />
                                        <span className="px-2 py-0.5 rounded-full bg-gray-800 text-gray-400">개인정보</span>
                                        <ChevronRight className="w-3 h-3 text-gray-600" />
                                        <span className="px-2 py-0.5 rounded-full bg-gray-800 text-gray-400">녹화</span>
                                    </div>
                                </div>

                                <div className="p-6 space-y-5">
                                    {/* 캠페인 이름 */}
                                    <div>
                                        <label className="text-xs text-gray-500 font-medium block mb-1.5">캠페인 이름</label>
                                        <div className="h-10 bg-gray-50 border border-gray-200 rounded-lg px-3 flex items-center text-sm text-gray-700 font-medium">
                                            결제 페이지 이탈 분석
                                        </div>
                                    </div>

                                    {/* 대상 URL */}
                                    <div>
                                        <label className="text-xs text-gray-500 font-medium block mb-1.5">녹화 대상 페이지</label>
                                        <div className="space-y-2">
                                            {[
                                                { type: '시작', url: 'https://shop.example.com/order/', kind: 'include' },
                                                { type: '포함', url: '/cart', kind: 'include' },
                                                { type: '시작', url: 'https://shop.example.com/mypage/', kind: 'exclude' },
                                            ].map((row, i) => (
                                                <div key={i} className={`flex items-center gap-2 p-2.5 rounded-lg border text-xs ${row.kind === 'exclude' ? 'border-red-200 bg-red-50/40' : 'border-gray-200 bg-gray-50/60'}`}>
                                                    <span className={`px-2 py-0.5 rounded font-bold ${row.kind === 'exclude' ? 'bg-red-100 text-red-600' : 'bg-sky-100 text-sky-700'}`}>
                                                        {row.kind === 'exclude' ? '제외' : '대상'}
                                                    </span>
                                                    <span className="text-gray-400">{row.type}</span>
                                                    <span className="font-mono text-gray-700 truncate">{row.url}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* 방문자 타게팅 + 최소 녹화 시간 */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="p-3 rounded-lg border border-gray-200 bg-white">
                                            <div className="text-[10px] text-gray-500 font-medium mb-2">방문자 타게팅</div>
                                            <div className="flex flex-wrap gap-1">
                                                <span className="px-2 py-0.5 rounded-full bg-gray-900 text-white text-[10px] font-bold flex items-center gap-1"><Smartphone className="w-2.5 h-2.5" />모바일</span>
                                                <span className="px-2 py-0.5 rounded-full bg-gray-900 text-white text-[10px] font-bold">유료 광고 유입</span>
                                            </div>
                                        </div>
                                        <div className="p-3 rounded-lg border border-gray-200 bg-white">
                                            <div className="text-[10px] text-gray-500 font-medium mb-2">최소 녹화 시간</div>
                                            <div className="flex gap-1">
                                                {['5초', '10초', '30초', '1분'].map((s, i) => (
                                                    <span key={s} className={`px-2 py-0.5 rounded text-[10px] font-bold ${i === 1 ? 'bg-sky-600 text-white' : 'bg-gray-100 text-gray-500'}`}>{s}</span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* 녹화 수 제한 */}
                                    <div className="p-3 rounded-lg border border-gray-200 bg-white flex items-center justify-between">
                                        <div>
                                            <div className="text-[10px] text-gray-500 font-medium">녹화 수 제한</div>
                                            <div className="text-sm font-bold text-gray-900">2,000 <span className="text-xs font-medium text-gray-400">세션 보관</span></div>
                                        </div>
                                        <div className="w-10 h-5 rounded-full bg-sky-600 relative">
                                            <div className="absolute right-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow"></div>
                                        </div>
                                    </div>

                                    <button className="w-full py-3 bg-gradient-to-r from-cyan-600 to-sky-600 text-white rounded-xl text-sm font-bold mt-1 flex items-center justify-center gap-2">
                                        다음: 개인정보 설정
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-sky-100 rounded-full blur-3xl -z-10"></div>
                            <div className="absolute -top-6 -right-6 w-32 h-32 bg-cyan-100 rounded-full blur-3xl -z-10"></div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 6: 개인정보 보호
      ========================================== */}
            <section className="py-24 bg-gray-50 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row items-center gap-16">

                        {/* Left: 설명 */}
                        <div className="flex-1 max-w-xl">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-6">
                                <ShieldCheck className="w-4 h-4" />
                                <span>Privacy by Default</span>
                            </div>

                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                                고객의 개인정보는<br />
                                <span className="text-emerald-600">애초에 저장되지 않습니다</span>
                            </h2>

                            <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                                방문자가 입력하는 모든 값은 브라우저에서 가려진 채로 전송됩니다.
                                마스킹은 설정으로 끌 수 없습니다. 켜는 걸 잊어서 생기는 사고가 구조적으로 불가능합니다.
                            </p>

                            <div className="space-y-6 mb-8">
                                {[
                                    { title: "모든 입력값 마스킹 — 해제 불가", desc: "이름, 전화번호, 주소, 검색어까지 입력 필드의 값은 전부 가려집니다. 비밀번호·카드번호는 말할 것도 없습니다.", icon: <EyeOff className="w-5 h-5 text-emerald-600" /> },
                                    { title: "가릴 요소 직접 선택", desc: "화면에 표시되는 이름·주문 내역처럼 입력값이 아닌 영역은 클릭 한 번으로 지정해 추가로 가립니다.", icon: <MousePointerClick className="w-5 h-5 text-emerald-600" /> },
                                    { title: "스크립트를 실행하지 않는 재생", desc: "재생 화면은 고객사 페이지를 다시 불러오지 않습니다. 녹화된 화면 구조만 그리므로 재생 중 외부 요청이나 추적 코드가 돌지 않습니다.", icon: <Lock className="w-5 h-5 text-emerald-600" /> }
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4">
                                        <div className="mt-1 w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                            {item.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                                            <p className="text-sm text-gray-600">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right: 마스킹 시각화 */}
                        <div className="flex-1 w-full">
                            <div className="grid sm:grid-cols-2 gap-4">
                                {/* 방문자 화면 */}
                                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                                    <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">방문자 화면</span>
                                        <Monitor className="w-3.5 h-3.5 text-gray-300" />
                                    </div>
                                    <div className="p-4 space-y-3">
                                        <div className="text-xs font-bold text-gray-800">배송지 입력</div>
                                        {[
                                            { label: '받는 분', value: '김민서' },
                                            { label: '연락처', value: '010-4821-7730' },
                                            { label: '주소', value: '서울시 마포구 월드컵로 12' },
                                        ].map((f, i) => (
                                            <div key={i}>
                                                <div className="text-[10px] text-gray-400 mb-1">{f.label}</div>
                                                <div className="h-8 rounded-md border border-gray-200 px-2.5 flex items-center text-xs text-gray-800">{f.value}</div>
                                            </div>
                                        ))}
                                        <div>
                                            <div className="text-[10px] text-gray-400 mb-1">카드번호</div>
                                            <div className="h-8 rounded-md border border-gray-200 px-2.5 flex items-center text-xs text-gray-800 font-mono">4532 1187 2290 6614</div>
                                        </div>
                                    </div>
                                </div>

                                {/* 재생 화면 */}
                                <div className="bg-gray-900 rounded-2xl shadow-xl border border-gray-800 overflow-hidden relative">
                                    <div className="px-4 py-2.5 border-b border-gray-800 flex items-center justify-between">
                                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">재생 화면</span>
                                        <div className="flex items-center gap-1 text-[10px] font-bold text-emerald-400">
                                            <ShieldCheck className="w-3 h-3" /> 마스킹됨
                                        </div>
                                    </div>
                                    <div className="p-4 space-y-3">
                                        <div className="text-xs font-bold text-gray-200">배송지 입력</div>
                                        {[
                                            { label: '받는 분', value: '***' },
                                            { label: '연락처', value: '*************' },
                                            { label: '주소', value: '****************' },
                                        ].map((f, i) => (
                                            <div key={i}>
                                                <div className="text-[10px] text-gray-500 mb-1">{f.label}</div>
                                                <div className="h-8 rounded-md border border-gray-700 bg-gray-800 px-2.5 flex items-center text-xs text-gray-400 font-mono tracking-widest">{f.value}</div>
                                            </div>
                                        ))}
                                        <div>
                                            <div className="text-[10px] text-gray-500 mb-1">카드번호</div>
                                            <div className="h-8 rounded-md border border-gray-700 bg-gray-800 px-2.5 flex items-center text-xs text-gray-400 font-mono tracking-widest">**** **** **** ****</div>
                                        </div>
                                    </div>
                                    <div className="absolute bottom-3 right-3 px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/20 text-[9px] text-emerald-400 font-bold">
                                        브라우저에서 이미 가려진 채 전송
                                    </div>
                                </div>
                            </div>

                            {/* 요소 선택 도구 힌트 */}
                            <div className="mt-4 bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-4">
                                <div className="relative w-20 h-14 rounded-lg bg-gray-50 border border-gray-200 flex-shrink-0 overflow-hidden">
                                    <div className="absolute left-2 top-2 w-10 h-2 rounded bg-gray-300"></div>
                                    <div className="absolute left-2 top-6 right-2 h-5 rounded border-2 border-dashed border-emerald-500 bg-emerald-50/70"></div>
                                    <MousePointer2 className="absolute right-3 bottom-1 w-3.5 h-3.5 text-gray-900 fill-white" />
                                </div>
                                <div>
                                    <div className="text-sm font-bold text-gray-900">화면에 보이는 정보도 클릭으로 가리기</div>
                                    <div className="text-xs text-gray-500 mt-0.5">주문 내역의 이름·주소처럼 입력 필드가 아닌 영역은 요소 선택 도구로 지정하면 같은 종류가 전부 마스킹됩니다.</div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 7: 세션 목록 — 누구의 세션인지 한눈에
      ========================================== */}
            <section className="py-24 bg-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row-reverse items-center gap-16">

                        {/* Right: 설명 (반전) */}
                        <div className="flex-1 max-w-xl">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-semibold mb-6">
                                <Users className="w-4 h-4" />
                                <span>Session List</span>
                            </div>

                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                                세션 목록에서<br />
                                <span className="text-cyan-600">맥락을 먼저</span> 읽으세요
                            </h2>

                            <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                                재생을 누르기 전에도 많은 것이 보입니다. 어디서 들어와 몇 페이지를 얼마나 봤는지,
                                어떤 기기였는지. 같은 방문자의 세션은 같은 별칭으로 표시되어 여러 세션을 이어서 볼 수 있습니다.
                            </p>

                            <div className="space-y-6 mb-8">
                                {[
                                    { title: "방문자 별칭", desc: "무작위 ID 대신 '차분한 수달'처럼 기억할 수 있는 이름. 같은 방문자는 항상 같은 이름입니다.", icon: <Users className="w-5 h-5 text-cyan-600" /> },
                                    { title: "유입 경로 · 환경", desc: "검색, 소셜, 광고, 직접 방문 여부와 디바이스·브라우저·OS를 세션마다 기록합니다.", icon: <Globe className="w-5 h-5 text-cyan-600" /> },
                                    { title: "즐겨찾기", desc: "팀에 공유할 세션은 별표로 표시해 두고 필터로 바로 모아 봅니다.", icon: <Star className="w-5 h-5 text-cyan-600" /> }
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4">
                                        <div className="mt-1 w-10 h-10 bg-cyan-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                            {item.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                                            <p className="text-sm text-gray-600">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Left: 세션 목록 목업 */}
                        <div className="flex-1 w-full">
                            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                                <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex justify-between items-center">
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">결제 페이지 이탈 분석</span>
                                    <div className="flex items-center gap-2 text-[10px]">
                                        <span className="text-gray-500">보관 중</span>
                                        <span className="font-mono text-gray-300">1,284 / 2,000</span>
                                        <div className="w-16 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                                            <div className="h-full w-[64%] bg-cyan-500 rounded-full"></div>
                                        </div>
                                    </div>
                                </div>

                                {/* 필터 바 */}
                                <div className="px-4 py-2.5 border-b border-gray-100 flex flex-wrap gap-1.5 bg-gray-50/60">
                                    {[
                                        { label: '전체', on: true },
                                        { label: 'PC', on: false },
                                        { label: '모바일', on: false },
                                        { label: '30초 이상', on: false },
                                        { label: '★ 즐겨찾기', on: false },
                                    ].map((f, i) => (
                                        <span key={i} className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${f.on ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-500 border-gray-200'}`}>{f.label}</span>
                                    ))}
                                </div>

                                <ul className="divide-y divide-gray-100">
                                    {[
                                        { name: '차분한 수달', suffix: 'm9x7', url: '/order/checkout', when: '12분 전', dur: '1:52', pages: 3, dev: <Smartphone className="w-3 h-3" />, env: 'Safari · iOS', src: '네이버 검색', fav: true, hot: true },
                                        { name: '고요한 여우', suffix: 'k2p8', url: '/product/128', when: '41분 전', dur: '0:48', pages: 2, dev: <Monitor className="w-3 h-3" />, env: 'Chrome · Windows', src: '직접 방문', fav: false },
                                        { name: '느긋한 펭귄', suffix: 'q7d2', url: '/order/checkout', when: '1시간 전', dur: '4:32', pages: 5, dev: <Smartphone className="w-3 h-3" />, env: 'Chrome · Android', src: '인스타그램', fav: true },
                                        { name: '차분한 수달', suffix: 'm9x7', url: '/cart', when: '어제 21:14', dur: '0:36', pages: 1, dev: <Smartphone className="w-3 h-3" />, env: 'Safari · iOS', src: '유료 광고', fav: false },
                                        { name: '씩씩한 고래', suffix: 'z1n4', url: '/order/complete', when: '어제 18:02', dur: '2:10', pages: 4, dev: <Tablet className="w-3 h-3" />, env: 'Safari · iPadOS', src: '카카오톡', fav: false },
                                    ].map((s, i) => (
                                        <li key={i} className={`group px-4 py-3 flex items-center gap-3 hover:bg-cyan-50/40 transition-colors ${s.hot ? 'bg-cyan-50/30' : ''}`}>
                                            <div className="w-8 h-8 rounded-full bg-gray-100 group-hover:bg-cyan-600 flex items-center justify-center text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                                                <Play className="w-3 h-3 fill-current ml-0.5" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold text-gray-900">{s.name} <span className="text-gray-400 font-mono font-normal">· {s.suffix}</span></span>
                                                    <span className="text-[10px] text-gray-400">{s.when}</span>
                                                </div>
                                                <div className="text-[10px] text-gray-400 font-mono truncate">{s.url}</div>
                                            </div>
                                            <div className="hidden sm:flex items-center gap-1.5">
                                                <span className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px] font-mono font-bold text-gray-600 flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{s.dur}</span>
                                                <span className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px] font-bold text-gray-600">{s.pages}p</span>
                                                <span className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px] font-medium text-gray-600 flex items-center gap-1">{s.dev}{s.env}</span>
                                                <span className="px-1.5 py-0.5 rounded bg-cyan-50 text-[10px] font-medium text-cyan-700">{s.src}</span>
                                            </div>
                                            <Star className={`w-4 h-4 flex-shrink-0 ${s.fav ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 8: 활용 사례
      ========================================== */}
            <section className="py-24 bg-slate-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-300 text-xs font-semibold uppercase tracking-wider mb-4">
                            <Search className="w-3 h-3" />
                            <span>Use Cases</span>
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                            숫자로는 보이지 않던 <br className="sm:hidden" />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-sky-500">진짜 원인</span>
                        </h2>
                        <p className="text-xl text-slate-400">
                            집계 데이터가 던진 질문에 <br className="sm:hidden" />세션 녹화가 답한 장면들입니다.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                        {useCases.map((item, index) => (
                            <div
                                key={index}
                                className="group relative bg-slate-800/50 border border-slate-700 rounded-2xl p-8 hover:bg-slate-800 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                            >
                                <div className={`absolute top-0 right-0 w-32 h-32 ${item.classes.glow} rounded-full blur-3xl -mr-10 -mt-10 group-hover:opacity-100 transition-opacity`}></div>

                                <div className="flex justify-between items-start mb-6 relative z-10">
                                    <div className={`p-3 rounded-xl bg-slate-900 border border-slate-700 ${item.classes.iconBox} group-hover:text-white transition-colors shadow-lg`}>
                                        {item.icon}
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${item.classes.badge}`}>
                                        {item.type}
                                    </span>
                                </div>

                                <h3 className="text-2xl font-bold text-white mb-6 relative z-10">
                                    {item.title}
                                </h3>

                                <div className="space-y-6 mb-8 relative z-10">
                                    <div className="flex gap-4">
                                        <div className="mt-1 min-w-[24px]">
                                            <AlertTriangle className="w-5 h-5 text-red-400" />
                                        </div>
                                        <div>
                                            <span className="text-xs font-bold text-red-400 uppercase tracking-wide block mb-1">숫자만 봤을 때</span>
                                            <p className="text-sm text-slate-300 leading-relaxed">{item.problem}</p>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <div className="mt-1 min-w-[24px]">
                                            <Video className="w-5 h-5 text-cyan-400" />
                                        </div>
                                        <div>
                                            <span className="text-xs font-bold text-cyan-400 uppercase tracking-wide block mb-1">세션을 재생했더니</span>
                                            <p className="text-sm text-slate-300 leading-relaxed">{item.solution}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="relative z-10 bg-slate-900 rounded-xl p-4 border border-slate-700 flex items-center justify-between group-hover:border-slate-600 transition-colors shadow-inner">
                                    <div>
                                        <div className="text-xs text-slate-500 mb-1 font-medium uppercase">Impact</div>
                                        <div className={`text-xl font-bold ${item.classes.result}`}>{item.result}</div>
                                    </div>
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${item.classes.arrow}`}>
                                        <ArrowUpRight className="w-5 h-5" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 9: 주요 특징 벤토 그리드
      ========================================== */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                            사이트는 가볍게, 기록은 끊김 없이
                        </h2>
                        <p className="text-xl text-gray-600">
                            녹화가 켜진 페이지에서만 동작하고, <br className="sm:hidden" />방문자의 여정은 페이지를 넘어도 이어집니다.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                        {/* 1. 끊김 없는 세션 (Large) */}
                        <div className="md:col-span-2 bg-gray-50 rounded-2xl p-8 border border-gray-200 relative overflow-hidden group hover:border-cyan-200 transition-colors">
                            <div className="absolute top-6 right-6 flex items-center gap-2 px-3 py-1 bg-white rounded-full border border-gray-200 shadow-sm">
                                <span className="relative flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                                </span>
                                <span className="text-xs font-bold text-gray-600">Recording</span>
                            </div>

                            <div className="relative z-10">
                                <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center mb-6 text-cyan-600">
                                    <Route className="w-6 h-6" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">페이지를 넘어도 한 세션</h3>
                                <p className="text-gray-600 leading-relaxed max-w-md">
                                    상품 → 장바구니 → 결제로 이동해도 하나의 세션으로 이어서 재생됩니다. <br className="hidden sm:block" />
                                    탭을 옮겼다 돌아와도, 브라우저를 닫았다 다시 열어도 <strong>같은 방문의 기록</strong>으로 묶입니다.
                                </p>
                            </div>

                            <div className="absolute right-0 bottom-0 opacity-10">
                                <svg width="200" height="100" viewBox="0 0 200 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0 80 C 50 80, 50 20, 100 20 C 150 20, 150 50, 200 50" stroke="currentColor" strokeWidth="4" className="text-cyan-500" />
                                </svg>
                            </div>
                        </div>

                        {/* 2. 보관 기간 (Dark Tall) */}
                        <div className="bg-gray-900 text-white rounded-2xl p-8 border border-gray-800 relative overflow-hidden group">
                            <div className="relative z-10 h-full flex flex-col justify-between">
                                <div>
                                    <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-6">
                                        <CalendarDays className="w-6 h-6 text-sky-400" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-2">플랜별 보관 기간</h3>
                                    <p className="text-gray-400 text-sm leading-relaxed">
                                        녹화된 세션은 플랜에 따라<br />
                                        <strong>최대 12개월</strong>까지 보관됩니다.<br />
                                        지난 분기 이탈 원인도<br />
                                        다시 꺼내 볼 수 있습니다.
                                    </p>
                                </div>
                                <div className="mt-6 pt-6 border-t border-gray-700 space-y-1.5 text-sm font-mono text-gray-400">
                                    <div className="flex justify-between"><span>Free</span><span className="text-gray-300">2개월</span></div>
                                    <div className="flex justify-between"><span>Standard</span><span className="text-gray-300">6개월</span></div>
                                    <div className="flex justify-between"><span>Enterprise</span><span className="text-sky-400 font-bold">12개월</span></div>
                                </div>
                            </div>
                        </div>

                        {/* 3. 가벼운 SDK */}
                        <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-all duration-300 group">
                            <div className="flex justify-between items-start mb-6">
                                <div className="w-12 h-12 bg-cyan-50 rounded-xl flex items-center justify-center text-cyan-600">
                                    <Zap className="w-6 h-6" />
                                </div>
                                <div className="text-xs font-mono text-gray-400 bg-gray-50 px-2 py-1 rounded">on demand</div>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">필요할 때만 로드</h3>
                            <p className="text-gray-600 text-sm">
                                녹화 라이브러리는 녹화 대상 페이지에서만 불러옵니다.<br />
                                나머지 페이지의 로딩 속도에는 영향이 없고, 추가 스크립트 설치도 필요 없습니다.
                            </p>
                        </div>

                        {/* 4. 비공개 저장 + 안전장치 */}
                        <div className="md:col-span-2 bg-gradient-to-r from-cyan-50 to-sky-50 rounded-2xl p-8 border border-cyan-100 flex flex-col sm:flex-row items-center gap-6">
                            <div className="flex-shrink-0 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm text-cyan-500">
                                <Server className="w-8 h-8" />
                            </div>
                            <div className="text-center sm:text-left">
                                <h3 className="text-xl font-bold text-gray-900 mb-2">비공개 저장소 · 권한 기반 열람</h3>
                                <p className="text-gray-600 text-sm">
                                    녹화 데이터는 공개 URL 없이 비공개 저장소에 보관되고, 프로젝트 구성원만 서명된 링크로 재생합니다.<br className="hidden sm:block" />
                                    세션당 용량 상한과 캠페인별 녹화 수 제한으로 예상 밖의 비용이 생기지 않습니다.
                                </p>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 10: 퍼널 → 히트맵 → 세션 녹화 → A/B 시너지
      ========================================== */}
            <section className="py-24 bg-white relative overflow-hidden">
                <div className="absolute top-1/2 left-0 w-96 h-96 bg-cyan-100/50 rounded-full blur-[100px] -translate-y-1/2 -translate-x-1/3"></div>
                <div className="absolute top-1/2 right-0 w-96 h-96 bg-sky-100/50 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">

                        {/* Left: 텍스트 */}
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-50 border border-cyan-100 text-cyan-600 text-sm font-bold mb-6">
                                <Zap className="w-4 h-4 fill-cyan-600" />
                                <span>Full Loop</span>
                            </div>

                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                                <span className="text-purple-500">퍼널</span>로 어디를,<br />
                                <span className="text-orange-500">히트맵</span>으로 경향을,<br />
                                <span className="text-cyan-600">세션 녹화</span>로 장면을,<br />
                                <span className="text-blue-600">A/B 테스트</span>로 검증하세요
                            </h2>

                            <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                                세션 녹화는 히트맵이 보여준 경향의 '실제 사례'를 찾는 도구입니다.
                                경향에서 사례로, 사례에서 가설로, 가설에서 검증까지 한 플랫폼에서 이어집니다.
                            </p>

                            <div className="space-y-4">
                                <div className="flex items-center gap-4 bg-purple-50/50 p-4 rounded-xl border border-purple-100">
                                    <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold">1</div>
                                    <div>
                                        <div className="font-bold text-gray-900">병목 발견 (Funnel)</div>
                                        <div className="text-sm text-gray-500">"결제 시작 → 완료에서 62% 이탈" → 위치 파악</div>
                                    </div>
                                </div>
                                <div className="pl-7"><ArrowDown className="w-5 h-5 text-gray-300" /></div>

                                <div className="flex items-center gap-4 bg-orange-50/50 p-4 rounded-xl border border-orange-100">
                                    <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-bold">2</div>
                                    <div>
                                        <div className="font-bold text-gray-900">경향 파악 (Heatmap)</div>
                                        <div className="text-sm text-gray-500">"우편번호 버튼 주변에 클릭이 몰림" → 의심 지점</div>
                                    </div>
                                </div>
                                <div className="pl-7"><ArrowDown className="w-5 h-5 text-gray-300" /></div>

                                <div className="flex items-center gap-4 bg-cyan-50/50 p-4 rounded-xl border border-cyan-100 ring-1 ring-cyan-200">
                                    <div className="w-10 h-10 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center font-bold">3</div>
                                    <div>
                                        <div className="font-bold text-gray-900">장면 확인 (Session Replay)</div>
                                        <div className="text-sm text-gray-500">"팝업이 화면 밖에서 열려 못 찾고 이탈" → 원인 확정</div>
                                    </div>
                                </div>
                                <div className="pl-7"><ArrowDown className="w-5 h-5 text-gray-300" /></div>

                                <div className="flex items-center gap-4 bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                                    <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">4</div>
                                    <div>
                                        <div className="font-bold text-gray-900">가설 검증 (A/B Test)</div>
                                        <div className="text-sm text-gray-500">"인라인 주소 검색 vs 기존 팝업" → 효과 측정</div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 flex flex-wrap gap-4">
                                <Link to="/service/funnel">
                                    <button className="flex items-center gap-2 text-purple-600 font-bold hover:text-purple-700 transition-colors group">
                                        퍼널 분석 보기
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </Link>
                                <Link to="/service/heatmap">
                                    <button className="flex items-center gap-2 text-orange-600 font-bold hover:text-orange-700 transition-colors group">
                                        히트맵 분석 보기
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </Link>
                                <Link to="/service/ab-test">
                                    <button className="flex items-center gap-2 text-blue-600 font-bold hover:text-blue-700 transition-colors group">
                                        A/B 테스트 보기
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </Link>
                            </div>
                        </div>

                        {/* Right: 시각화 다이어그램 */}
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-3xl transform -rotate-3 scale-105 opacity-60 blur-xl"></div>

                            <div className="relative bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-xl flex flex-col items-center justify-center h-full min-h-[480px]">
                                <div className="grid grid-cols-2 gap-x-10 gap-y-8 w-full mb-8">
                                    <div className="text-center group">
                                        <div className="w-20 h-20 bg-purple-50 border border-purple-200 rounded-2xl flex items-center justify-center mb-3 mx-auto shadow-sm group-hover:scale-110 transition-transform">
                                            <Filter className="w-10 h-10 text-purple-500 fill-purple-500" />
                                        </div>
                                        <div className="font-bold text-gray-900">병목 발견</div>
                                        <div className="text-xs text-gray-500 mt-1 font-medium">퍼널 분석</div>
                                    </div>
                                    <div className="text-center group">
                                        <div className="w-20 h-20 bg-orange-50 border border-orange-200 rounded-2xl flex items-center justify-center mb-3 mx-auto shadow-sm group-hover:scale-110 transition-transform">
                                            <Flame className="w-10 h-10 text-orange-500 fill-orange-500" />
                                        </div>
                                        <div className="font-bold text-gray-900">경향 파악</div>
                                        <div className="text-xs text-gray-500 mt-1 font-medium">히트맵 분석</div>
                                    </div>
                                    <div className="text-center group">
                                        <div className="w-20 h-20 bg-cyan-50 border border-cyan-300 ring-4 ring-cyan-100 rounded-2xl flex items-center justify-center mb-3 mx-auto shadow-sm group-hover:scale-110 transition-transform">
                                            <Video className="w-10 h-10 text-cyan-500 fill-cyan-500" />
                                        </div>
                                        <div className="font-bold text-gray-900">장면 확인</div>
                                        <div className="text-xs text-cyan-600 mt-1 font-bold">세션 녹화</div>
                                    </div>
                                    <div className="text-center group">
                                        <div className="w-20 h-20 bg-blue-50 border border-blue-200 rounded-2xl flex items-center justify-center mb-3 mx-auto shadow-sm group-hover:scale-110 transition-transform">
                                            <Split className="w-10 h-10 text-blue-500" />
                                        </div>
                                        <div className="font-bold text-gray-900">가설 검증</div>
                                        <div className="text-xs text-gray-500 mt-1 font-medium">A/B 테스트</div>
                                    </div>
                                </div>

                                <div className="bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg animate-bounce flex items-center gap-2 z-10">
                                    <span>🚀</span>
                                    <span>추측 없는 전환율 개선</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 하단 연동 카드 */}
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-gradient-to-br from-orange-50 to-red-50/30 p-8 rounded-2xl border border-orange-100 hover:shadow-lg transition-all group">
                            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 text-orange-600 shadow-sm group-hover:scale-110 transition-transform">
                                <Layers className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">히트맵 + 세션 녹화</h3>
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                                히트맵에서 이상하게 클릭이 몰리는 영역을 발견했다면, 같은 페이지의 세션을 재생해
                                그 클릭이 무엇을 하려던 것이었는지 확인하세요. 경향과 사례를 나란히 놓으면 원인이 명확해집니다.
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-purple-50 to-violet-50/30 p-8 rounded-2xl border border-purple-100 hover:shadow-lg transition-all group">
                            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 text-purple-600 shadow-sm group-hover:scale-110 transition-transform">
                                <Filter className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">퍼널 + 세션 녹화</h3>
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                                퍼널에서 이탈이 큰 단계의 URL로 녹화 캠페인을 만들고, 방문 URL 필터로 그 페이지를 거친 세션만 모아 보세요.
                                이탈 직전 몇 초에 답이 있는 경우가 대부분입니다.
                            </p>
                        </div>
                    </div>

                </div>
            </section>

            {/* ==========================================
          섹션 11: CTA Section
      ========================================== */}
            <section className="relative py-32 overflow-hidden bg-slate-900">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyan-900/40 via-slate-900 to-slate-900"></div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-cyan-600/20 rounded-full blur-[120px] pointer-events-none"></div>
                <div className="absolute top-20 left-20 w-1 h-1 bg-white rounded-full animate-pulse"></div>
                <div className="absolute top-40 right-40 w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse delay-700"></div>

                <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-sm font-medium mb-8">
                        <Play className="w-4 h-4 fill-cyan-500" />
                        <span>Press play</span>
                    </div>

                    <h2 className="text-3xl md:text-6xl font-bold text-white mb-8 tracking-tight leading-tight">
                        고객이 떠나던 순간을 <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-sky-500">지금 다시 재생하세요.</span>
                    </h2>

                    <p className="text-lg md:text-xl text-slate-400 mb-12 max-w-2xl mx-auto leading-relaxed">
                        이탈에는 항상 장면이 있습니다.<br className="hidden md:block" />
                        그 장면을 보면, <br className="sm:hidden" />무엇을 고쳐야 할지는 저절로 보입니다.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link to="/freestart" className="w-full sm:w-auto">
                            <button className="w-full px-8 py-4 bg-gradient-to-r from-cyan-500 to-sky-600 hover:from-cyan-400 hover:to-sky-500 text-white rounded-xl font-bold text-lg shadow-[0_0_40px_-10px_rgba(6,182,212,0.35)] hover:shadow-[0_0_60px_-15px_rgba(6,182,212,0.55)] hover:-translate-y-1 transition-all flex items-center justify-center gap-2 group">
                                무료로 시작하기
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </Link>
                        <Link to="/contact" className="w-full sm:w-auto">
                            <button className="w-full px-8 py-4 bg-transparent border border-slate-700 text-slate-300 rounded-xl font-medium hover:bg-slate-800 hover:text-white transition-all">
                                도입 문의하기
                            </button>
                        </Link>
                    </div>

                    <div className="mt-12 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-slate-500 font-medium">
                        <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-cyan-500" /> 30일 무료 체험
                        </div>
                        <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-cyan-500" /> 신용카드 불필요
                        </div>
                        <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-cyan-500" /> 입력값 마스킹 기본 적용
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ServiceReplay;